/**
 * Every document this run read, and what it read from each.
 *
 * The Sources tab answers the question a reviewer asks before any single
 * number: what was this built from? Manual entries are listed alongside the
 * documents — a value a person typed has a source too, and it is the person.
 */

import { FileText, UserPen } from "lucide-react";
import React from "react";

import type { MonitoringRun } from "../../types/monitoring";
import { formatEpochMs } from "./runFormat";

export interface SourcesListProps {
	run: MonitoringRun;
	/** Opens a `doppler://` URI in the PDF viewer. */
	onOpenUri: (href: string, filename: string) => void;
}

/** The anchor for a document with no figure-level page: its first cited page. */
export function sourceDocumentHref(fileId: string, page: number | undefined) {
	const base = `doppler://pdf/${encodeURIComponent(fileId)}`;
	return page ? `${base}?page=${page}` : base;
}

const SourcesList: React.FC<SourcesListProps> = ({ run, onOpenUri }) => {
	const labelByKey = new Map(run.figures.map((f) => [f.key, f.label]));
	const manual = run.figures.filter((figure) => figure.state === "manual");

	return (
		<div className="flex flex-col gap-4">
			{run.sources.length === 0 ? (
				<p className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-sm text-text-muted">
					No document was read for this run.
				</p>
			) : (
				<ul className="flex flex-col gap-2">
					{run.sources.map((source) => (
						<li
							key={source.file_id}
							data-testid={`source-${source.file_id}`}
							className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3"
						>
							<FileText
								className="mt-0.5 h-4 w-4 shrink-0 text-text-muted"
								aria-hidden="true"
							/>
							<div className="min-w-0">
								<div className="flex flex-wrap items-center gap-2">
									<button
										type="button"
										onClick={() =>
											onOpenUri(
												sourceDocumentHref(source.file_id, source.pages[0]),
												source.filename,
											)
										}
										className="text-base text-text-base hover:underline"
									>
										{source.filename}
									</button>
									<span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-text-muted">
										{source.doc_type}
									</span>
								</div>
								<p className="mt-1 text-sm text-text-muted">
									{source.figure_keys.length} figure
									{source.figure_keys.length === 1 ? "" : "s"} read
									{source.figure_keys.length > 0
										? `: ${source.figure_keys
												.map((key) => labelByKey.get(key) ?? key)
												.join(", ")}`
										: ""}
									{source.pages.length > 0
										? ` · page${source.pages.length === 1 ? "" : "s"} ${source.pages.join(", ")}`
										: ""}
								</p>
							</div>
						</li>
					))}
				</ul>
			)}

			{manual.length > 0 && (
				<section>
					<h2 className="text-base font-medium text-text-base">
						Entered by a person
					</h2>
					<ul className="mt-2 flex flex-col gap-2">
						{manual.map((figure) => (
							<li
								key={figure.key}
								data-testid={`source-manual-${figure.key}`}
								className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3"
							>
								<UserPen
									className="mt-0.5 h-4 w-4 shrink-0 text-primary-600"
									aria-hidden="true"
								/>
								<div className="min-w-0">
									<p className="text-base text-text-base">
										Entered by {figure.entered_by_name ?? "a person"}
									</p>
									<p className="text-sm text-text-muted">
										{figure.label}
										{figure.note ? ` · ${figure.note}` : ""}
										{figure.entered_at
											? ` · ${formatEpochMs(figure.entered_at)}`
											: ""}
									</p>
								</div>
							</li>
						))}
					</ul>
				</section>
			)}
		</div>
	);
};

export default SourcesList;
